
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { useAuth } from './context/AuthContext';
import SplashScreen from './components/SplashScreen';
import ExpiredCenterAccessGate from './components/ExpiredCenterAccessGate';
import { isNativePlatform } from './platform/runtime';

// Code splitting por ruta: un dispositivo nuevo ya no descarga todo el portal,
// gráficas, administración, pagos y QR antes de poder mostrar Login/Panel.
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const MembersPage = lazy(() => import('./pages/MembersPage'));
const MemberListPage = lazy(() => import('./pages/members/MemberListPage'));
const MemberFormPage = lazy(() => import('./pages/members/MemberFormPage'));
const MemberDetailPage = lazy(() => import('./pages/members/MemberDetailPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const ProgrammingPage = lazy(() => import('./pages/ProgrammingPage'));
const FinancesPage = lazy(() => import('./pages/FinancesPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdministrationPage = lazy(() => import('./pages/AdministrationPage'));
const ScheduledClassFormPage = lazy(() => import('./pages/ScheduledClassFormPage'));
const PaymentFormPage = lazy(() => import('./pages/PaymentFormPage'));
const StaffClassesPage = lazy(() => import('./pages/StaffClassesPage'));
const MembershipsPage = lazy(() => import('./pages/MembershipsPage'));
const SystemUsersPage = lazy(() => import('./pages/SystemUsersPage'));
const PortalPage = lazy(() => import('./pages/PortalPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const MyPaymentsPage = lazy(() => import('./pages/MyPaymentsPage'));
const StudentCentersPage = lazy(() => import('./pages/StudentCentersPage'));
const BenchmarksPage = lazy(() => import('./pages/BenchmarksPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const AttendanceQRPage = lazy(() => import('./pages/AttendanceQRPage'));
const CheckInPage = lazy(() => import('./pages/CheckInPage'));
const ClassDetailPage = lazy(() => import('./pages/ClassDetailPage'));
const CentersPage = lazy(() => import('./pages/CentersPage'));
const CoachRankingsPage = lazy(() => import('./pages/CoachRankingsPage'));
const CenterClassHistoryPage = lazy(() => import('./pages/CenterClassHistoryPage'));
const CenterMetricsPage = lazy(() => import('./pages/CenterMetricsPage'));
const GlobalMembersPage = lazy(() => import('./pages/GlobalMembersPage'));
const GlobalCommentsAdminPage = lazy(() => import('./pages/GlobalCommentsAdminPage'));
const PulsoraBillingAdminPage = lazy(() => import('./pages/PulsoraBillingAdminPage'));
const CenterPulsoraSubscriptionPage = lazy(() => import('./pages/CenterPulsoraSubscriptionPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const InvitationPage = lazy(() => import('./pages/InvitationPage'));
const MemberImportPage = lazy(() => import('./pages/MemberImportPage'));
const PublicMemberProfilePage = lazy(() => import('./pages/PublicMemberProfilePage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage'));
const PersonalProgrammingPage = lazy(() => import('./pages/PersonalProgrammingPage'));
const MemberInformationPage = lazy(() => import('./pages/MemberInformationPage'));
const MemberActivityPage = lazy(() => import('./pages/MemberActivityPage'));
const MemberSettingsPage = lazy(() => import('./pages/MemberSettingsPage'));
const CenterInformationPage = lazy(() => import('./pages/CenterInformationPage'));
const CenterSettingsPage = lazy(() => import('./pages/CenterSettingsPage'));
const CenterNotificationsPage = lazy(() => import('./pages/CenterNotificationsPage'));
const CenterPublicProfilePage = lazy(() => import('./pages/CenterPublicProfilePage'));
const PublicLegalPage = lazy(() => import('./pages/PublicLegalPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));

const ProtectedRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  if (loading) return <SplashScreen />;
  if (!user) return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}` }} replace />;
  if (user.center_access_blocked) {
    // PULSORA EXPIRED CENTER ACCESS GATE V1
    if (user.center_access_blocked_reason === 'membership_expired') {
      return <ExpiredCenterAccessGate>{children}</ExpiredCenterAccessGate>;
    }

    return <div style={{minHeight:'100vh',display:'grid',placeItems:'center',padding:'24px',background:'#f6f8fc'}}>
      <div style={{maxWidth:560,padding:'32px',border:'1px solid #dfe5f1',borderRadius:18,background:'#fff',textAlign:'center',boxShadow:'0 18px 45px rgba(12,23,64,.08)'}}>
        <h1 style={{marginTop:0,color:'#0C1740'}}>Centro no disponible</h1>
        <p style={{color:'#5d6884',lineHeight:1.6}}>El Centro asociado a esta cuenta está archivado, eliminado o fuera de operación. No es posible entrar a su perfil operativo.</p>
        <button type="button" onClick={() => logout()} style={{marginTop:10,padding:'11px 18px',border:0,borderRadius:10,background:'#1457F4',color:'#fff',fontWeight:800,cursor:'pointer'}}>Cerrar sesión</button>
      </div>
    </div>;
  }
  return children;
};

const RoleRoute = ({ roles, children }) => {
  const { user } = useAuth();
  if (user?.global_access) {
    if (roles.includes('Global') || (user.global_master && roles.includes('Master'))) return children;
    return <Navigate to="/panel" replace />;
  }
  if (!roles.includes(user?.role)) return <Navigate to="/panel" replace />;
  return children;
};

const GlobalAccessRoute = ({ children }) => {
  const { user } = useAuth();
  return user?.global_access ? children : <Navigate to="/panel" replace />;
};

const AttendanceCapabilityRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.global_access) return children;
  const centerOperator = Boolean(user?.active_center) && ['Propietario', 'Administrador', 'Profesor'].includes(user?.role);
  const personalProfessor = Boolean(user?.member_id && user?.staff_id && !user?.active_center);
  return centerOperator || personalProfessor ? children : <Navigate to="/panel" replace />;
};

const MembersRouter = () => {
  const { user } = useAuth();
  return user?.global_access ? <GlobalMembersPage /> : <MembersPage />;
};

const PanelPage = () => {
  const { user } = useAuth();
  const personalProfile = !user?.global_access && Boolean(user?.member_id) && !user?.active_center;
  return personalProfile || user?.role === 'Profesor' ? <PortalPage /> : <DashboardPage />;
};

const RankingPage = () => {
  const { user } = useAuth();
  if (user?.global_access) return <LeaderboardPage />;
  const personalProfile = Boolean(user?.member_id) && !user?.active_center;
  return personalProfile ? <LeaderboardPage /> : <CoachRankingsPage />;
};



const RootEntry = () => {
  const { user, loading } = useAuth();
  if (!isNativePlatform()) return <LandingPage />;
  if (loading) return <SplashScreen />;
  return <Navigate to={user ? '/panel' : '/login'} replace />;
};

function App() {
  return (
    <Suspense fallback={<SplashScreen />}>
    <Routes>
      <Route path="/" element={<RootEntry />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PublicLegalPage />} />
      <Route path="/terms" element={<PublicLegalPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/invitation/:token" element={<InvitationPage />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        
        <Route path="panel" element={<PanelPage />} />
        <Route path="dashboard" element={<Navigate to="/panel" replace />} />
        <Route path="portal" element={<Navigate to="/panel" replace />} />
        <Route path="ranking" element={<RankingPage />} />
        <Route path="leaderboard" element={<Navigate to="/ranking" replace />} />
        <Route path="community" element={<RoleRoute roles={['Global', 'Master', 'Propietario', 'Profesor', 'Administrador', 'Alumno']}><CommunityPage /></RoleRoute>} />
        <Route path="coach-rankings" element={<Navigate to="/ranking" replace />} />
        <Route path="store" element={<Navigate to="/panel" replace />} />
        <Route path="centers" element={<RoleRoute roles={['Global', 'Propietario', 'Administrador']}><CentersPage /></RoleRoute>} />
        <Route path="my-payments" element={<RoleRoute roles={['Alumno']}><MyPaymentsPage /></RoleRoute>} />
        <Route path="my-centers" element={<RoleRoute roles={['Alumno']}><StudentCentersPage /></RoleRoute>} />
        <Route path="member-profile" element={<RoleRoute roles={['Alumno']}><MemberProfilePage /></RoleRoute>} />
        <Route path="my-programming" element={<RoleRoute roles={['Alumno']}><PersonalProgrammingPage /></RoleRoute>} />
        <Route path="information" element={<RoleRoute roles={['Alumno']}><MemberInformationPage /></RoleRoute>} />
        <Route path="activity" element={<RoleRoute roles={['Alumno']}><MemberActivityPage /></RoleRoute>} />
        <Route path="settings" element={<RoleRoute roles={['Alumno']}><MemberSettingsPage /></RoleRoute>} />
        <Route path="center-information" element={<RoleRoute roles={['Propietario', 'Administrador']}><CenterInformationPage /></RoleRoute>} />
        <Route path="center-settings" element={<RoleRoute roles={['Propietario', 'Administrador']}><CenterSettingsPage /></RoleRoute>} />
        <Route path="center-notifications" element={<RoleRoute roles={['Propietario', 'Administrador']}><CenterNotificationsPage /></RoleRoute>} />
        <Route path="center-profile/:id" element={<CenterPublicProfilePage />} />
        <Route path="my-benchmarks" element={<RoleRoute roles={['Alumno']}><BenchmarksPage /></RoleRoute>} />
        <Route path="history" element={<RoleRoute roles={['Alumno']}><HistoryPage /></RoleRoute>} />
        <Route path="profile/members/:id" element={<PublicMemberProfilePage />} />
        <Route path="support" element={<SupportPage />} />
        
        {/* Nested routes for members module */}
        <Route path="members" element={<RoleRoute roles={['Global', 'Propietario', 'Administrador', 'Recepción']}><MembersRouter /></RoleRoute>}>
          <Route index element={<MemberListPage />} />
          <Route path="new" element={<MemberFormPage />} />
          <Route path=":id" element={<MemberDetailPage />} />
          <Route path=":id/programacion" element={<RoleRoute roles={['Propietario']}><PersonalProgrammingPage /></RoleRoute>} />
          <Route path=":id/edit" element={<GlobalAccessRoute><MemberFormPage /></GlobalAccessRoute>} />
        </Route>

        <Route path="schedule" element={<SchedulePage />} />
        <Route path="programming" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><ProgrammingPage /></RoleRoute>} />
        <Route path="class-history" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador', 'Profesor']}><CenterClassHistoryPage /></RoleRoute>} />
        <Route path="administration/metrics" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><CenterMetricsPage /></RoleRoute>} />
        <Route path="schedule/:id" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador', 'Profesor', 'Alumno']}><ClassDetailPage /></RoleRoute>} />
        <Route path="schedule/new" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador', 'Profesor']}><ScheduledClassFormPage /></RoleRoute>} />
        <Route path="schedule/:id/edit" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador', 'Profesor']}><ScheduledClassFormPage /></RoleRoute>} />
        <Route path="attendance/:id" element={<AttendanceCapabilityRoute><AttendanceQRPage /></AttendanceCapabilityRoute>} />
        <Route path="check-in" element={<RoleRoute roles={['Alumno']}><CheckInPage /></RoleRoute>} />
        <Route path="finances" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><FinancesPage /></RoleRoute>} />
        <Route path="finances/new" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><PaymentFormPage /></RoleRoute>} />
        <Route path="finances/:id/edit" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><PaymentFormPage /></RoleRoute>} />
        <Route path="administration" element={<RoleRoute roles={['Global', 'Propietario', 'Administrador']}><AdministrationPage /></RoleRoute>} />
        <Route path="administration/staff-classes" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><StaffClassesPage /></RoleRoute>} />
        <Route path="administration/memberships" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><MembershipsPage /></RoleRoute>} />
        <Route path="administration/member-import" element={<RoleRoute roles={['Master', 'Propietario', 'Administrador']}><MemberImportPage /></RoleRoute>} />
        <Route path="administration/users" element={<RoleRoute roles={['Global', 'Propietario', 'Administrador']}><SystemUsersPage /></RoleRoute>} />
        <Route path="administration/join-requests" element={<Navigate to="/members" replace />} />
        <Route path="administration/global-comments" element={<GlobalAccessRoute><GlobalCommentsAdminPage /></GlobalAccessRoute>} />
        <Route path="administration/pulsora-billing" element={<GlobalAccessRoute><PulsoraBillingAdminPage /></GlobalAccessRoute>} />
        <Route path="administration/pulsora-subscription" element={<RoleRoute roles={['Propietario', 'Administrador']}><CenterPulsoraSubscriptionPage /></RoleRoute>} />
        <Route path="administration/center-subscriptions" element={<Navigate to="/administration/pulsora-billing" replace />} />
        
        <Route path="*" element={<h2>Página no encontrada</h2>} />
      </Route>
    </Routes>
    </Suspense>
  );
}
export default App;
